import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useCerberus } from "../../hooks/useCerberus";
import Loading from "../Widgets/Loading";
import { audienceForRoles } from "./audience";
import NoRolesAssigned from "./NoRolesAssigned";

/** The only audiences /register has a form for. Everyone else is created by invitation. */
const REGISTERS_ITSELF = ["patient", "external"];

const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { isRegistered, roles: userRoles, loading } = useCerberus();

  if (loading) return <Loading />;

  // First check: if not registered, redirect to register — but only for an audience that HAS a
  // registration form. /register renders per-audience, so sending clinical staff or a caregiver
  // there was a dead end: no branch matched and they got the 404 page with no way forward. It is
  // unreachable today (the API derives their isRegistered from "a Users row exists", which is
  // always true by the time they can sign in) and stayed invisible for that reason; a server-side
  // registration gate for either audience — likely for caregivers, whose portal is new — would
  // have turned it into a hard lockout. Falling through instead lands them on the page they asked
  // for, or on the no-roles card below if that is the real problem.
  const audience = audienceForRoles(userRoles);
  if (!isRegistered && audience && REGISTERS_ITSELF.includes(audience)) {
    return <Navigate to="/register" />;
  }

  // Second check: a signed-in account with no role gets the explanation, not the page.
  if (!userRoles || userRoles.length === 0) {
    return <NoRolesAssigned />;
  }

  return children;
};

export default RequireAuth;
